// tools/SculptingTool.ts
import { ITool } from './ITool';
import HalfEdgeMesh from '@/modules/three/common/halfEdgeMesh/HalfEdgeMesh.js';
import { Intersection, Vector3 } from 'three';
import { BrushVisualizer } from './BrushVisualizer';
import sculpt, { SculptOpts } from '@/thirdPartyLibraries/three-mesh-halfedge/src/operations/sculpt';
import { threeInteractionStore } from '../store/threeInteractionStore';
import { ThreeRootState } from '../common/BaseThreeScene';
import { threeStore } from '../store/threeStore';

export class SculptingTool implements ITool {
  readonly name = 'sculpt';
  private dragging = false;
  private lastPoint = new Vector3();
  private spacing = 0.25;
  private accum = 0;
  private brush?: BrushVisualizer;

  onActivate() { this.dragging = false; this.accum = 0; }
  onDeactivate() { this.dragging = false; this.accum = 0; this.brush?.group.removeFromParent(); this.brush?.dispose(); this.brush = undefined; }

  onPointerDown(ev: PointerEvent, hit: Intersection, three: ThreeRootState): void {
    const opts = threeInteractionStore.getState().sculptMode;
    if (!opts || !hit || !(hit.object instanceof HalfEdgeMesh)) { this.dragging = false; return; }
    this.ensureBrush(three);
    this.updateBrush(hit, opts);
    this.dragging = true;
    threeStore.getState().rootState.controls.enabled = false;
    this.accum = 0;
    this.lastPoint.copy(hit.point);
    this.strokeAt(hit, opts);
  }

  onPointerMove(ev: PointerEvent, hit: Intersection, three: ThreeRootState): void {
    const opts = threeInteractionStore.getState().sculptMode;
    if (!opts) return;
    if (!hit || !(hit.object instanceof HalfEdgeMesh)) { this.brush?.setVisible(false); return; }
    this.ensureBrush(three);
    this.updateBrush(hit, opts);
    if (this.dragging) {
      const d = hit.point.distanceTo(this.lastPoint);
      this.accum += d;
      if (this.accum >= this.spacing) {
        this.accum = 0;
        this.lastPoint.copy(hit.point);
        this.strokeAt(hit, opts);
      }
    }
  }

  onPointerUp(ev: PointerEvent, hit: Intersection, three: ThreeRootState): void {
    threeStore.getState().rootState.controls.enabled = true;
    this.dragging = false;
  }

  // --- helpers (stejné jako předtím) ---
  private ensureBrush(three: ThreeRootState) {
    if (!this.brush) {
      this.brush = new BrushVisualizer();
      three.scene.add(this.brush.group);
    }
    this.brush.setVisible(true);
  }

  private updateBrush(hit: Intersection, opts: SculptOpts) {
    const mesh = hit.object as HalfEdgeMesh;
    const v = mesh.getClosestVertexToIntersection(hit);
    const el = v ? this.avgEdgeLen(v) : 1;
    const radius = (opts.area && opts.area > 0) ? opts.area : Math.max(1e-6, (opts.rings ?? 1) * el);
    this.brush!.setRadius(radius);
    this.brush!.setFalloff(opts.falloff ?? 'smooth', opts.sigma);
    this.brush!.setPose(hit.point, hit.face?.normal ?? hit.normal ?? new Vector3(0,0,1));
  }

  private strokeAt(hit: Intersection, opts: SculptOpts) {
    const mesh = hit.object as HalfEdgeMesh;
    const v = mesh.getClosestVertexToIntersection(hit);
    if (!v) return;
    const viewDir = new Vector3();
    threeStore.getState().rootState.camera.getWorldDirection(viewDir);
    opts.direction = viewDir;
    opts.planePoint = hit.point;
    const changed = sculpt([v], opts);
    if (changed > 0) { mesh.updateGeometryFromHalfedges(); mesh.setNewGeometry(mesh.geometry); }
  }


  private avgEdgeLen(v: any) {
    let sum = 0, cnt = 0;
    for (const u of v.oneRing()) { sum += u.position.distanceTo(v.position); cnt++; }
    return cnt ? Math.max(1e-6, sum / cnt) : 1;
  }
}